// src/security/documentLimits.catalog.ts
// Límites mensuales de documentos por tipo de suscripción.
// SuscriptionType.documentLimits guarda un JSON { documentTypeId -> tope mensual }.
// Semántica: ausente, 0 o valor no positivo => ilimitado (backwards-compat).

/**
 * Mensaje único mostrado cuando un tenant alcanza el tope mensual de su plan.
 * Se reutiliza en el frontend (cotizaciones) y como fallback del error del lambda.
 */
export const DOCUMENT_LIMIT_REACHED_MESSAGE =
    "Ya utilizó la cantidad máxima de documentos asignados a su plan. " +
    "Por favor actualice su plan para poder crear un documento.";

/** Mapa documentTypeId -> tope mensual, tal como se guarda en SuscriptionType.documentLimits. */
export type DocumentLimits = Record<string, number> | null;

export type DocumentLimitNode = {
    /** ID estable (coincide con el documentType del schema/lambda). No cambiar una vez publicado. */
    id: string;
    /** Etiqueta para la UI de configuración y comparación de planes. */
    label: string;
    /** Etiqueta corta usada al formatear "N cotizaciones / mes". */
    unitLabel: string;
    /**
     * Campo de fecha del modelo usado para contar por mes (sort key del GSI listXByFechaTenant).
     * Invoice/BuyInvoice/Quote usan "fecha" (YYYY-MM-DD); Creditnote/Debitnote usan "created"
     * (YYYY-MM-DD HH:mm:ss). Ambos permiten filtrar el mes con beginsWith("YYYY-MM").
     */
    dateField: "fecha" | "created";
    /** queryField del índice por fecha para contar el mes desde el frontend. */
    queryField: string;
    /** true = se valida en el frontend (no pasa por el lambda save-edocument), ej. Quote. */
    frontendOnly?: boolean;
};

/**
 * Catálogo de tipos de documento con tope mensual configurable.
 * Los ids coinciden con SaveEDocumentType del lambda (salvo Quote, que es frontend-only).
 */
export const DOCUMENT_LIMIT_CATALOG: DocumentLimitNode[] = [
    {
        id: "Invoice",
        label: "Facturas electrónicas",
        unitLabel: "facturas",
        dateField: "fecha",
        queryField: "listInvoiceByFechaTenant",
    },
    {
        id: "Creditnote",
        label: "Notas de crédito",
        unitLabel: "notas de crédito",
        dateField: "created",
        queryField: "listCreditnoteByFechaTenant",
    },
    {
        id: "Debitnote",
        label: "Notas de débito",
        unitLabel: "notas de débito",
        dateField: "created",
        queryField: "listDebitnoteByFechaTenant",
    },
    {
        id: "BuyInvoice",
        label: "Facturas de compra",
        unitLabel: "facturas de compra",
        dateField: "fecha",
        queryField: "listBuyInvoiceByFechaTenant",
    },
    {
        id: "Quote",
        label: "Cotizaciones",
        unitLabel: "cotizaciones",
        dateField: "fecha",
        queryField: "listQuoteByFechaTenant",
        frontendOnly: true,
    },
];

/** Parsea el campo documentLimits (string JSON u objeto) de forma tolerante. */
export function parseDocumentLimits(raw: unknown): DocumentLimits {
    if (raw == null) return null;
    const source = typeof raw === "string" ? tryParse(raw) : raw;
    if (typeof source !== "object" || source === null) return null;
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
        const num = Number(value);
        if (Number.isFinite(num) && num > 0) out[key] = Math.floor(num);
    }
    return Object.keys(out).length ? out : null;
}

function tryParse(raw: string): unknown {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/** Tope mensual para un tipo de documento. 0 = ilimitado (ausente o valor no positivo). */
export function getDocumentLimit(limits: DocumentLimits | undefined, documentTypeId: string): number {
    if (!limits) return 0;
    const value = Number(limits[documentTypeId] ?? 0);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

/** Prefijo "YYYY-MM" del mes actual, usado en beginsWith para contar documentos del mes. */
export function currentMonthPrefix(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    return `${year}-${month}`;
}

/** Formatea "5 facturas / mes" para destacar el límite en la comparación de planes. */
export function formatLimitLabel(documentTypeId: string, limit: number): string {
    const node = DOCUMENT_LIMIT_CATALOG.find((n) => n.id === documentTypeId);
    const unit = node?.unitLabel ?? "documentos";
    return `${limit} ${unit} / mes`;
}
